import { FilterSettingIcon } from '@/assets/icons/filterSetting';
import Button from '@/components/basicComponents/button';
import Tabs, { ITab } from '@/components/basicComponents/tabs';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitle } from '@/components/header/pageTitle';
import { TableColumns } from '@/components/table/types';
import { useResponsive } from '@/hooks/use-responsive';
import { MainWrap, PaddingWrap } from '@/styles/common';
import { themeVar } from '@/styles/theme';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import styled from 'styled-components';
import { ColumnsDisplayed } from './components/columnsDisplayed';
import { SelectedColumnsList } from './components/selectedColumnsList';

const mobileTabs: ITab[] = [
  { value: 'Columns Displayed', amount: null },
  { value: 'Display Order', amount: null }
];

interface TableColumnsEditorProps {
  allColumns: TableColumns[];
  columnsList: TableColumns[];
  tableName: string;
  setColumnsList: Dispatch<SetStateAction<TableColumns[]>>;
  onClose: () => void;
}

export const TableColumnsEditor = ({ allColumns, columnsList, tableName, setColumnsList, onClose }: TableColumnsEditorProps) => {
  const { isMobile } = useResponsive();

  const [activeTab, setActiveTab] = useState(mobileTabs[0].value);
  const [newColumns, setNewColumns] = useState(columnsList.map((column) => ({ ...column })));

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, []);

  const saveNewColumns = () => {
    const isFixedColumnExists = newColumns.some((column: TableColumns) => column.isFixed);

    if (isFixedColumnExists) {
      setColumnsList(newColumns);
    } else {
      const fixedTableColumns = allColumns.filter((column) => column.isFixed);
      setColumnsList([...fixedTableColumns, ...newColumns]);
    }
    const updatedOrder = newColumns.map((column) => {
      return { cellKey: column.cellKey, isHidden: column.isHidden };
    });
    localStorage.setItem(`${tableName}ColumnsOrder`, JSON.stringify(updatedOrder));
    onClose();
  };

  const MainContent = (
    <ModalContentWrapper>
      <ColumnsDisplayed newColumns={newColumns} setNewColumns={setNewColumns} />
      <SelectedColumnsList allColumns={allColumns} newColumns={newColumns} setNewColumns={setNewColumns} />
    </ModalContentWrapper>
  );

  const ActionButtonsContent = (
    <ActionButtons>
      <Button onClick={onClose} styleType="outline" size="md">
        Cancel
      </Button>
      <Button onClick={saveNewColumns} size="md">
        Apply
      </Button>
    </ActionButtons>
  );

  if (isMobile) {
    return (
      <MainWrap>
        <PaddingWrap>
          <MobileHeaderContainer>
            <MobileTitle>
              <FilterSettingIcon fill={themeVar('font.strong')} width={29} height={29} />
              Choose Which Columns You See
            </MobileTitle>
            <Cross width="max-content" src="/static/icons/blue-cross.svg" alt="Cross" onClick={onClose} />
          </MobileHeaderContainer>
          <TabsWrapper>
            <Tabs tabs={mobileTabs} activeTab={activeTab} setActiveTab={setActiveTab} />
          </TabsWrapper>
          {activeTab === 'Columns Displayed' && <ColumnsDisplayed newColumns={newColumns} setNewColumns={setNewColumns} />}
          {activeTab === 'Display Order' && <SelectedColumnsList allColumns={allColumns} newColumns={newColumns} setNewColumns={setNewColumns} />}
          {ActionButtonsContent}
        </PaddingWrap>
      </MainWrap>
    );
  }

  return (
    <MainWrap>
      <Header modalControl={<GoBackButton handleClose={onClose} />} />
      <PageTitle title="Choose Which Columns You See" />
      <PaddingWrap>
        <PageContent>
          {MainContent}
          {ActionButtonsContent}
        </PageContent>
      </PaddingWrap>
    </MainWrap>
  );
};

const PageContent = styled.div`
  margin-top: 20px;
`;

const ModalContentWrapper = styled.div`
  display: flex;
  gap: 30px;
`;

const MobileTitle = styled.div`
  display: flex;
  align-items: center;
  font-weight: 600;
  font-size: 20px;
  gap: 6px;
  color: ${themeVar('font.strong')};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  padding: 30px 60px;
`;

const TabsWrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: 5px;
  padding-top: 30px;
`;

const Cross = styled.img<{ width: string }>`
  cursor: pointer;
  margin-left: auto;
  width: ${({ width }) => width};
`;

const MobileHeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 20px;
`;
